/**
 * Video Autoplay Module - Media-Lifecycle Compatible
 *
 * Purpose:
 * - Lazy loading and autoplay/pause videos based on viewport visibility
 * - Single-Medium Enforcement via StoryMediaManager
 * - Sound support after user gesture (Autoplay Policy)
 * - Panel-aware: Videos stop when panel deactivated
 *
 * Media-Lifecycle Integration:
 * - Implements Module Contract (init/start/pause/stop/cleanup)
 * - Registers with StoryMediaManager
 * - Coordinates with other media (Audio Bubbles, 360°, etc.)
 *
 * Dependencies: StoryCore, StoryMediaManager
 * File: assets/js/story-module-video-autoplay.js
 */

(function (window) {
  'use strict';

  // Global sound enabled flag (set after first user interaction)
  var soundEnabled = false;
  var currentPlayingVideo = null;

  // Enable sound after first user gesture (required by browser autoplay policy)
  document.addEventListener('click', function() {
    soundEnabled = true;
    // Retroactively unmute any already-playing video (started muted before user gesture)
    if (currentPlayingVideo && currentPlayingVideo.muted) {
      currentPlayingVideo.muted = false;
    }
  }, { once: true });

  class VideoAutoplay {
    constructor(core) {
      this.core = core;
      this.videos = [];
      this.videoStates = new WeakMap();

      // Module Contract Properties
      this.name = 'video-autoplay';
      this.type = 'video';
      this.isPlaying = false;
      this.panel = null;
      this.element = null;  // Current playing video element

      this.initialize();
    }

    initialize() {
      // Internal initialization (called from constructor)
      // This finds all videos and sets up observers
      if (!this.core.root) return;

      // Find all videos with autoplay control
      this.videos = Array.from(
        this.core.root.querySelectorAll('video[data-story-autoplay]')
      );

      if (this.videos.length === 0) {
        return;
      }

      // Setup video elements
      this.setupVideos();

      // Setup event listeners
      this.setupVideoListeners();

      // Observe all videos with media observer (30% threshold)
      this.core.observe(this.videos, 'media');

      // Register with core
      this.core.registerModule('videoAutoplay', this);

      // Register with MediaManager
      this.registerWithMediaManager();
    }

    registerWithMediaManager() {
      if (window.StoryMediaManager) {
        window.StoryMediaManager.register(this);

        if (window.location.search.includes('debug=story')) {
          console.log('[VideoAutoplay] Registered with MediaManager');
        }
      }
    }

    setupVideos() {
      var self = this;
      this.videos.forEach(function(video) {
        // Ensure proper attributes
        video.setAttribute('playsinline', '');
        // Start muted (will be unmuted after user gesture when playing)
        video.setAttribute('muted', '');

        // Mobile: always force preload=none to save memory.
        // Desktop: respect existing preload attribute, default to none.
        if (window.innerWidth <= 768) {
          video.setAttribute('preload', 'none');
        } else if (!video.hasAttribute('preload')) {
          video.setAttribute('preload', 'none');
        }

        // Auto-generate mobile/desktop sources from single <source>
        self._autoSources(video);

        // Auto-generate poster thumbnail from video URL
        self._autoPoster(video);

        // Initialize state
        self.videoStates.set(video, {
          hasPlayed: false,
          isPlaying: false,
          preloadSet: false
        });
      });
    }

    /**
     * Auto-generate mobile + desktop <source> elements.
     * Convention: desktop = video.mp4, mobile = video_m.mp4
     * Only runs if exactly one <source> without media attribute exists.
     * Manually set media attributes are respected (backward compatible).
     */
    _autoSources(video) {
      var sources = video.querySelectorAll('source');

      // Only auto-generate if exactly one source without media attribute
      if (sources.length !== 1) return;
      if (sources[0].hasAttribute('media')) return;

      var desktopSrc = sources[0].getAttribute('src') || '';
      if (!desktopSrc || desktopSrc.indexOf('.mp4') === -1) return;

      var mobileSrc = desktopSrc.replace(/\.mp4$/i, '_m.mp4');

      // Add media attribute to existing source (becomes desktop)
      sources[0].setAttribute('media', '(min-width: 769px)');

      // Create mobile source, insert before desktop (browser picks first match)
      var mobileSource = document.createElement('source');
      mobileSource.setAttribute('src', mobileSrc);
      mobileSource.setAttribute('type', 'video/mp4');
      mobileSource.setAttribute('media', '(max-width: 768px)');
      video.insertBefore(mobileSource, sources[0]);
    }

    /**
     * Auto-generate responsive poster from video URLs.
     * Convention:
     *   Desktop video.mp4      → video_thumb.jpg
     *   Mobile  video_m.mp4    → video_m_thumb.jpg
     * Poster switches on viewport change (rotation, resize).
     * Skipped if poster is already set manually.
     */
    _autoPoster(video) {
      // Manual poster takes priority
      if (video.hasAttribute('poster')) return;

      // Find desktop + mobile source URLs
      var sources = video.querySelectorAll('source');
      var desktopSrc = '';
      var mobileSrc = '';
      for (var i = 0; i < sources.length; i++) {
        var s = sources[i].getAttribute('src') || '';
        if (s.indexOf('_m.mp4') !== -1) {
          mobileSrc = s;
        } else if (s.indexOf('.mp4') !== -1) {
          desktopSrc = s;
        }
      }

      // Need at least a desktop source
      if (!desktopSrc) return;

      var desktopPoster = desktopSrc.replace(/\.mp4$/i, '_thumb.jpg');
      var mobilePoster = mobileSrc
        ? mobileSrc.replace(/\.mp4$/i, '_thumb.jpg')
        : desktopPoster;  // Fallback: same poster for both

      // Set poster based on current viewport
      function applyPoster() {
        var isMobile = window.innerWidth <= 768;
        video.setAttribute('poster', isMobile ? mobilePoster : desktopPoster);
      }

      applyPoster();

      // Update on resize/rotation (only if posters differ)
      if (mobilePoster !== desktopPoster) {
        window.addEventListener('resize', applyPoster);
      }
    }

    setupVideoListeners() {
      var self = this;
      this.videos.forEach(function(video) {
        video.addEventListener('story:media:enter', function() {
          self.handleVideoEnter(video);
        });

        video.addEventListener('story:media:exit', function() {
          self.handleVideoExit(video);
        });

        // Error handling
        video.addEventListener('error', function(e) {
          console.warn('Video error:', video.src, e);
        });
      });
    }

    handleVideoEnter(video) {
      var self = this;
      var state = this.videoStates.get(video);

      // Update panel reference to the video's actual panel.
      // Without this, MediaManager thinks the video belongs to the
      // previously activated panel and stops it on panel deactivation.
      var videoPanel = video.closest('.story-panel');
      if (videoPanel) {
        this.panel = videoPanel;
      }

      // Request permission from MediaManager
      if (window.StoryMediaManager) {
        var allowed = window.StoryMediaManager.requestPlay(this);
        if (!allowed) {
          if (window.location.search.includes('debug=story')) {
            console.warn('[VideoAutoplay] Play request denied by MediaManager');
          }
          return;
        }
      }

      // Stop any other currently playing video (only one video at a time)
      if (currentPlayingVideo && currentPlayingVideo !== video) {
        currentPlayingVideo.pause();
        currentPlayingVideo.muted = true;
        var otherState = self.videoStates.get(currentPlayingVideo);
        if (otherState) {
          otherState.isPlaying = false;
        }
      }

      // Switch preload to auto on first visibility
      if (!state.preloadSet) {
        video.setAttribute('preload', 'auto');
        state.preloadSet = true;

        // Preload next video in advance for seamless experience
        self.preloadNextVideo(video);

        // Wait a bit for preload to start, then try to play
        setTimeout(function() {
          self.tryPlayVideo(video, state);
        }, 100);
      } else {
        this.tryPlayVideo(video, state);
      }
    }

    preloadNextVideo(currentVideo) {
      var self = this;
      // Find current video index
      var currentIndex = this.videos.indexOf(currentVideo);

      // If there's a next video, preload it
      if (currentIndex !== -1 && currentIndex < this.videos.length - 1) {
        var nextVideo = this.videos[currentIndex + 1];
        var nextState = this.videoStates.get(nextVideo);

        // Only preload if not already done
        if (nextState && !nextState.preloadSet) {
          nextVideo.setAttribute('preload', 'auto');
          nextState.preloadSet = true;
        }
      }
    }

    tryPlayVideo(video, state) {
      var self = this;
      // Unmute video if sound is enabled (after user gesture)
      if (soundEnabled) {
        video.muted = false;
      }

      var playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.then(function() {
          state.isPlaying = true;
          state.hasPlayed = true;
          // Track this as the currently playing video
          currentPlayingVideo = video;

          // Update module state
          self.isPlaying = true;
          self.element = video;
        }).catch(function(error) {
          // Autoplay prevented by browser - this is expected for unmuted videos
          // or on certain browsers/contexts
          if (error.name === 'NotAllowedError') {
            console.log('Video autoplay prevented (expected for unmuted or restricted context)');
          } else {
            console.warn('Video play error:', error);
          }
        });
      }
    }

    handleVideoExit(video) {
      var state = this.videoStates.get(video);

      if (state.isPlaying) {
        video.pause();
        video.muted = true;
        state.isPlaying = false;

        // Clear current playing video reference
        if (currentPlayingVideo === video) {
          currentPlayingVideo = null;
        }
      }

      // Videos continue from where they left off when scrolling back
      // (currentTime is NOT reset)

      // Update module state
      this.isPlaying = false;
      this.element = null;

      // Notify MediaManager so ambient audio can unduck
      if (window.StoryMediaManager) {
        window.StoryMediaManager.notifyStop(this);
      }
    }

    /* ----------------------------------------------------------
       Media-Lifecycle Hooks (Module Contract)
       ---------------------------------------------------------- */

    // Module Contract Methods (required by MediaManager)

    init(panel) {
      // Called when panel is activated
      // Note: Video Autoplay is global (observes all videos at once)
      // So we don't need per-panel initialization
      // But we store panel reference for compatibility
      if (panel) {
        this.panel = panel;
      }
    }

    start(video) {
      // Start playing a specific video element
      // Called by MediaManager or manually
      if (!video || !(video instanceof HTMLVideoElement)) {
        console.warn('[VideoAutoplay] start() called without valid video element');
        return;
      }

      // Delegate to handleVideoEnter (which handles MediaManager coordination)
      this.handleVideoEnter(video);
    }

    pause() {
      // Called by MediaManager when another medium starts or panel deactivated
      if (currentPlayingVideo) {
        currentPlayingVideo.pause();
        currentPlayingVideo.muted = true;

        var state = this.videoStates.get(currentPlayingVideo);
        if (state) {
          state.isPlaying = false;
        }

        this.isPlaying = false;
        this.element = null;

        // Notify MediaManager
        if (window.StoryMediaManager) {
          window.StoryMediaManager.notifyStop(this);
        }
      }
    }

    stop() {
      // Full stop with reset (same as pause for videos)
      this.pause();
      // Videos keep their currentTime (don't reset to 0)
    }

    cleanup() {
      // Free resources - called on panel deactivation
      this.pause();
      currentPlayingVideo = null;
    }

    destroy() {
      var self = this;
      this.videos.forEach(function(video) {
        self.core.unobserve([video], 'media');
        video.pause();
      });
      this.videos = [];
      this.videoStates = new WeakMap();
    }
  }

  // Expose VideoAutoplay to window for other modules
  window.StoryModuleVideoAutoplay = VideoAutoplay;

})(window);
